/**
 * Widgets Customizados para Templates
 * 
 * Componentes especializados para campos com lógica complexa:
 * - CPF com validação e formatação
 * - Fornecedor com botão de adicionar
 * - Cargo carregado dinamicamente
 * - Unidade/Setor carregado do banco
 */

import React, { useState, useEffect } from 'react'
import { cn } from '../../../utils/formatter'
import { Check, X, Loader2 } from 'lucide-react'
import {
  getSetoresEUnidades,
  getCargos,
  getFornecedores,
  adicionarFornecedor,
  validarCPF,
  validarCNPJ,
  formatarCPF,
  formatarCNPJ,
  type Fornecedor,
} from '../services/templateDataService'
import { UserSelect } from '../../../components/Select/UserSelect'
import type { UserOption } from '../../../components/Select/types'
import { apiService } from '../../demands/services/api'

const inputClasses = cn(
  'w-full px-3 py-2 text-sm rounded-lg border transition-colors duration-200',
  'bg-white dark:bg-neutral-800',
  'border-gray-300 dark:border-neutral-600',
  'text-gray-900 dark:text-gray-100',
  'placeholder:text-gray-400 dark:placeholder:text-gray-500',
  'focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500',
  'disabled:opacity-50 disabled:cursor-not-allowed'
)

const selectClasses = cn(
  inputClasses,
  'appearance-none cursor-pointer',
  'bg-[url("data:image/svg+xml,%3csvg xmlns=%27http://www.w3.org/2000/svg%27 fill=%27none%27 viewBox=%270 0 20 20%27%3e%3cpath stroke=%27%236b7280%27 stroke-linecap=%27round%27 stroke-linejoin=%27round%27 stroke-width=%271.5%27 d=%27M6 8l4 4 4-4%27/%3e%3c/svg%3e")] bg-no-repeat bg-[right_0.5rem_center] bg-[length:1.5em_1.5em] pr-10'
)

/**
 * Widget de CPF com validação e formatação automática
 */
export const CPFWidget: React.FC<{
  id: string
  value: string
  onChange: (value: string) => void
  disabled?: boolean
  readonly?: boolean
  placeholder?: string
}> = ({ id, value, onChange, disabled, readonly, placeholder }) => {
  const [formatted, setFormatted] = useState('')
  const [isValid, setIsValid] = useState<boolean | null>(null)

  useEffect(() => {
    if (value) {
      const numeros = value.replace(/[^\d]/g, '')
      setFormatted(formatarCPF(numeros))
      setIsValid(validarCPF(numeros))
    } else {
      setFormatted('')
      setIsValid(null)
    }
  }, [value])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value.replace(/[^\d]/g, '').slice(0, 11)
    onChange(input)
  }

  return (
    <div className="relative">
      <input
        id={id}
        type="text"
        value={formatted}
        onChange={handleChange}
        disabled={disabled || readonly}
        placeholder={placeholder}
        maxLength={14}
        className={cn(
          inputClasses,
          isValid === false && 'border-red-500 focus:ring-red-500/20 focus:border-red-500',
          isValid === true && 'border-green-500 focus:ring-green-500/20 focus:border-green-500'
        )}
      />
      {isValid === false && (
        <p className="text-xs text-red-500 mt-1">CPF inválido</p>
      )}
      {isValid === true && (
        <p className="text-xs text-green-600 mt-1">CPF válido</p>
      )}
    </div>
  )
}

/**
 * Widget de seleção de Fornecedor com botão para adicionar novo
 */
export const FornecedorSelectorWidget: React.FC<{
  id: string
  value: string
  onChange: (value: string) => void
  disabled?: boolean
  readonly?: boolean
  placeholder?: string
}> = ({ id, value, onChange, disabled, readonly, placeholder }) => {
  const [fornecedores, setFornecedores] = useState<Array<{ value: string; label: string }>>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [novoFornecedor, setNovoFornecedor] = useState<Fornecedor>({
    nome: '',
    razao_social: '',
    cnpj: '',
  })
  const [cnpjValido, setCnpjValido] = useState<boolean | null>(null)

  useEffect(() => {
    loadFornecedores()
  }, [])

  const loadFornecedores = async () => {
    setIsLoading(true)
    const data = await getFornecedores()
    setFornecedores(data)
    setIsLoading(false)
  }

  const handleCnpjChange = (cnpj: string) => {
    const numeros = cnpj.replace(/[^\d]/g, '')
    setNovoFornecedor(prev => ({ ...prev, cnpj: numeros }))
    setCnpjValido(numeros.length === 14 ? validarCNPJ(numeros) : null)
  }

  const handleSalvarFornecedor = async () => {
    if (!novoFornecedor.nome || !novoFornecedor.razao_social || !novoFornecedor.cnpj) {
      alert('Preencha todos os campos')
      return
    }

    if (!validarCNPJ(novoFornecedor.cnpj)) {
      alert('CNPJ inválido')
      return
    }

    setIsSaving(true)
    const sucesso = await adicionarFornecedor(novoFornecedor)
    setIsSaving(false)

    if (sucesso) {
      await loadFornecedores()
      onChange(novoFornecedor.cnpj)
      setShowModal(false)
      setNovoFornecedor({ nome: '', razao_social: '', cnpj: '' })
      setCnpjValido(null)
    } else {
      alert('Erro ao adicionar fornecedor')
    }
  }

  return (
    <>
      <div className="flex gap-2">
        <select
          id={id}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled || readonly || isLoading}
          className={cn(selectClasses, 'flex-1')}
        >
          <option value="">{isLoading ? 'Carregando...' : placeholder}</option>
          {fornecedores.map(f => (
            <option key={f.value} value={f.value}>
              {f.label}
            </option>
          ))}
        </select>
        {/* <button
          type="button"
          onClick={() => setShowModal(true)}
          disabled={disabled || readonly}
          className={cn(
            'px-3 py-2 rounded-lg border border-gray-300 dark:border-neutral-600',
            'bg-white dark:bg-neutral-800 hover:bg-gray-50 dark:hover:bg-neutral-700',
            'text-gray-700 dark:text-gray-300',
            'transition-colors duration-200',
            'disabled:opacity-50 disabled:cursor-not-allowed'
          )}
          title="Adicionar novo fornecedor"
        >
          <Plus className="w-4 h-4" />
        </button> */}
      </div>

      {/* Modal de adicionar fornecedor */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">
              Adicionar Novo Fornecedor
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1.5 text-gray-700 dark:text-gray-300">
                  Nome <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={novoFornecedor.nome}
                  onChange={(e) => setNovoFornecedor(prev => ({ ...prev, nome: e.target.value }))}
                  className={inputClasses}
                  placeholder="Nome do fornecedor"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5 text-gray-700 dark:text-gray-300">
                  Razão Social <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={novoFornecedor.razao_social}
                  onChange={(e) => setNovoFornecedor(prev => ({ ...prev, razao_social: e.target.value }))}
                  className={inputClasses}
                  placeholder="Razão social"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5 text-gray-700 dark:text-gray-300">
                  CNPJ <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formatarCNPJ(novoFornecedor.cnpj)}
                  onChange={(e) => handleCnpjChange(e.target.value)}
                  className={cn(
                    inputClasses,
                    cnpjValido === false && 'border-red-500',
                    cnpjValido === true && 'border-green-500'
                  )}
                  placeholder="00.000.000/0000-00"
                  maxLength={18}
                />
                {cnpjValido === false && (
                  <p className="text-xs text-red-500 mt-1">CNPJ inválido</p>
                )}
                {cnpjValido === true && (
                  <p className="text-xs text-green-600 mt-1">CNPJ válido</p>
                )}
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <button
                type="button"
                onClick={handleSalvarFornecedor}
                disabled={isSaving || !cnpjValido}
                className={cn(
                  'flex-1 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700',
                  'text-white font-medium text-sm transition-colors duration-200',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                  'flex items-center justify-center gap-2'
                )}
              >
                {isSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Salvando...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Salvar
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowModal(false)
                  setNovoFornecedor({ nome: '', razao_social: '', cnpj: '' })
                  setCnpjValido(null)
                }}
                disabled={isSaving}
                className={cn(
                  'px-4 py-2 rounded-lg border border-gray-300 dark:border-neutral-600',
                  'bg-white dark:bg-neutral-800 hover:bg-gray-50 dark:hover:bg-neutral-700',
                  'text-gray-700 dark:text-gray-300 font-medium text-sm',
                  'transition-colors duration-200',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                  'flex items-center gap-2'
                )}
              >
                <X className="w-4 h-4" />
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

/**
 * Widget de seleção de Cargo (carregado de fonte externa)
 */
export const CargoSelectorWidget: React.FC<{
  id: string
  value: string
  onChange: (value: string) => void
  disabled?: boolean
  readonly?: boolean
  placeholder?: string
}> = ({ id, value, onChange, disabled, readonly, placeholder }) => {
  const [cargos, setCargos] = useState<Array<{ value: string; label: string }>>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const loadCargos = async () => {
      setIsLoading(true)
      const data = await getCargos()
      setCargos(data)
      setIsLoading(false)
    }
    loadCargos()
  }, [])

  return (
    <select
      id={id}
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled || readonly || isLoading}
      className={selectClasses}
    >
      <option value="">{isLoading ? 'Carregando...' : placeholder}</option>
      {cargos.map(cargo => (
        <option key={cargo.value} value={cargo.value}>
          {cargo.label}
        </option>
      ))}
    </select>
  )
}

/**
 * Widget de seleção de Unidade/Setor (carregado do banco)
 */
export const UnidadeSelectorWidget: React.FC<{
  id: string
  value: string
  onChange: (value: string) => void
  disabled?: boolean
  readonly?: boolean
  placeholder?: string
}> = ({ id, value, onChange, disabled, readonly, placeholder }) => {
  const [unidades, setUnidades] = useState<Array<{ value: string; label: string }>>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const loadUnidades = async () => {
      setIsLoading(true)
      const data = await getSetoresEUnidades()
      setUnidades(data)
      setIsLoading(false)
    }
    loadUnidades()
  }, [])

  return (
    <select
      id={id}
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled || readonly || isLoading}
      className={selectClasses}
    >
      <option value="">{isLoading ? 'Carregando...' : placeholder}</option>
      {unidades.map(unidade => (
        <option key={unidade.value} value={unidade.value}>
          {unidade.label}
        </option>
      ))}
    </select>
  )
}

/**
 * Widget de seleção de Usuário do sistema (usando UserSelect com avatar e email)
 */
export const UserSelectorWidget: React.FC<{
  id: string
  value: string
  onChange: (value: string) => void
  disabled?: boolean
  readonly?: boolean
  placeholder?: string
}> = ({ value, onChange, disabled, readonly, placeholder }) => {
  const [options, setOptions] = useState<UserOption[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const loadUsers = async () => {
      setIsLoading(true)
      try {
        const users = await apiService.getUsuarios()
        const userOptions: UserOption[] = users.map(u => ({
          value: u.name,
          label: u.name,
          email: u.email,
        }))
        setOptions(userOptions)
      } catch {
        setOptions([])
      } finally {
        setIsLoading(false)
      }
    }
    loadUsers()
  }, [])

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 text-sm text-gray-500 dark:text-gray-400 rounded-lg border border-gray-300 dark:border-neutral-600 bg-white dark:bg-neutral-800">
        <Loader2 className="w-4 h-4 animate-spin" />
        Carregando usuários...
      </div>
    )
  }

  return (
    <UserSelect
      variant="user"
      options={options}
      value={value || ''}
      onChange={(val) => onChange(String(val))}
      placeholder={placeholder || 'Selecione um usuário...'}
      disabled={!!(disabled || readonly)}
      searchable
    />
  )
}
